/**
 * AIReferencesSubTab V2
 *
 * 4 stabile KI-Referenzbilder pro Produkt fuer die KI-Bildgenerierung.
 * Mit AssetPickerModal fuer Bildauswahl und Rollenzuweisung.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { Sparkles, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, Loading, Alert, AlertDescription } from '@/components/v2/ui';
import { AssetPickerModal } from './AssetPickerModal';
import { AIRefSlotCard } from './AIRefSlotCard';
import { useAIReferences } from './useAIReferences';
import type { RoleOption } from './RoleSelector';

interface AIReferencesSubTabProps {
  productId: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared constant used by sibling components
export const AI_ROLES: RoleOption[] = [
  { value: 'PACKSHOT', label: 'Packshot', icon: '📦', color: 'bg-orange-600/90' },
  { value: 'LIFESTYLE', label: 'Lifestyle', icon: '🌿', color: 'bg-green-600/90' },
  { value: 'DETAIL', label: 'Detail', icon: '📸', color: 'bg-primary-600/90' },
  { value: 'BRAND', label: 'Brand', icon: '🏷️', color: 'bg-primary-600/90' },
];

export function AIReferencesSubTab({ productId }: AIReferencesSubTabProps) {
  const { t } = useTranslation();
  const {
    slots, assets, loading, error,
    activePickerSlot, saving, setActivePickerSlot,
    handlePickerResult, handleClearSlot,
  } = useAIReferences(productId);

  const filledCount = slots.filter((s) => !s.is_empty).length;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loading size="lg" />
        <p className="mt-4 text-neutral-600 dark:text-neutral-400">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <AIReferencesHeader filledCount={filledCount} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {slots.map((slot) => (
          <AIRefSlotCard
            key={slot.slot}
            slot={slot}
            roles={AI_ROLES}
            saving={saving === slot.slot}
            onOpenPicker={() => setActivePickerSlot(slot.slot)}
            onClear={() => handleClearSlot(slot.slot)}
          />
        ))}
      </div>

      {activePickerSlot !== null && (
        <AssetPickerModal
          assets={assets}
          roles={AI_ROLES}
          onSelect={handlePickerResult}
          onClose={() => setActivePickerSlot(null)}
          slotNumber={activePickerSlot}
          productId={productId}
        />
      )}

      <AIReferencesTips />
    </div>
  );
}

// ============================================================================
// Header sub-component
// ============================================================================

function AIReferencesHeader({ filledCount }: { filledCount: number }) {
  return (
    <div className="flex justify-between items-center mb-4">
      <div>
        <h6 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          KI-Referenzbilder
        </h6>
        <span className="text-sm text-neutral-600 dark:text-neutral-400">
          Diese 4 Bilder werden fuer die KI-Bildgenerierung verwendet
        </span>
      </div>
      <Badge variant="secondary">{filledCount} / 4 belegt</Badge>
    </div>
  );
}

// ============================================================================
// Tips sub-component
// ============================================================================

function AIReferencesTips() {
  return (
    <Alert variant="info">
      <Lightbulb className="h-4 w-4" />
      <AlertDescription>
        <strong className="font-semibold">Tipps fuer beste Ergebnisse:</strong>
        <ul className="mt-2 space-y-1 text-sm">
          <li>Verwenden Sie hochaufloesende Bilder (min. 512x512px)</li>
          <li>Waehlen Sie unterschiedliche Perspektiven (Packshot, Lifestyle, Detail)</li>
          <li>Produktbilder ohne stoerenden Hintergrund funktionieren am besten</li>
        </ul>
      </AlertDescription>
    </Alert>
  );
}
